import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import BottomNav from "../components/BottomNav";
import { toast } from "sonner";
import {
  Plus, Users, Crown, Smartphone, Clock, Radio, Copy, Trash2, Edit3,
  Calendar, ChevronRight, X, Check, MoreHorizontal, Play, Pause,
} from "lucide-react";

export default function CommandCenter() {
  const [summary, setSummary] = useState(null);
  const [groups, setGroups] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [s, g, d] = await Promise.all([
        api.get("/hub/summary"),
        api.get("/groups"),
        api.get("/devices/manage"),
      ]);
      setSummary(s.data);
      setGroups(g.data || []);
      setDevices(d.data?.devices?.filter((x) => !x.is_master) || []);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Proximity Elite required");
        navigate("/dashboard", { replace: true });
        return;
      }
      toast.error("Failed to load Command Center");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const onCreateDone = () => { setShowCreate(false); setEditing(null); load(); };

  const activate = async (g) => {
    try { await api.post(`/groups/${g.group_id}/activate`); toast.success(`${g.name} is live`); load(); }
    catch { toast.error("Failed"); }
  };
  const deactivate = async (g) => {
    try { await api.post(`/groups/${g.group_id}/deactivate`); toast.message(`${g.name} deactivated`); load(); }
    catch { toast.error("Failed"); }
  };
  const duplicate = async (g) => {
    try { await api.post(`/groups/${g.group_id}/duplicate`); toast.success("Group duplicated"); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/groups/${g.group_id}`); toast.success("Group deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center"><div className="label-eyebrow">Loading Command Center…</div></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 pb-28">
      <header className="px-6 pt-10 pb-6">
        <div className="label-eyebrow flex items-center gap-2"><Crown size={12} className="text-[#FFCC00]"/> Proximity Elite</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Command Center</h1>
        <p className="text-sm text-zinc-400 mt-1">Organize devices into groups. Schedule, activate, manage at scale.</p>
      </header>

      {/* Summary grid */}
      <section className="px-6 grid grid-cols-2 lg:grid-cols-4 gap-2.5">
        <Stat label="Total Devices" value={summary?.devices_connected ?? 0} sub={`of ${summary?.max_devices ?? 65}`} icon={Smartphone} color="#34C759"/>
        <Stat label="Open Slots" value={summary?.open_slots ?? 0} sub="available" icon={Plus} color="#FFCC00"/>
        <Stat label="Groups" value={summary?.groups_total ?? 0} sub="total" icon={Users} color="#007AFF"/>
        <Stat label="Active Now" value={summary?.groups_active ?? 0} sub="live groups" icon={Radio} color="#FF3B30"/>
      </section>

      {/* Create */}
      <section className="px-6 mt-6">
        <button
          data-testid="create-group-btn"
          onClick={() => { setEditing(null); setShowCreate(true); }}
          className="w-full p-4 rounded-2xl border border-[#007AFF]/40 bg-gradient-to-br from-[#007AFF]/15 to-transparent hover:from-[#007AFF]/22 flex items-center gap-3"
        >
          <div className="size-11 rounded-xl bg-[#007AFF] grid place-items-center">
            <Plus size={20} className="text-white"/>
          </div>
          <div className="flex-1 text-left">
            <div className="font-display font-bold text-lg">Create Group</div>
            <div className="text-xs text-zinc-400">Live now or scheduled — assign any of your devices</div>
          </div>
          <ChevronRight size={18} className="text-[#007AFF]"/>
        </button>
      </section>

      {/* Groups list */}
      <section className="px-6 mt-6">
        <div className="label-eyebrow mb-3 flex items-center justify-between">
          <span>Groups · {groups.length}</span>
          <Link to="/devices" data-testid="hub-goto-devices" className="text-[#007AFF] normal-case tracking-normal text-[11px] font-bold">Manage devices →</Link>
        </div>
        {groups.length === 0 ? (
          <div className="p-8 rounded-2xl border border-dashed border-white/10 text-center text-zinc-500 text-sm">
            No groups yet. Create your first group to organize devices.
          </div>
        ) : (
          <div className="space-y-2.5">
            {groups.map((g) => (
              <GroupCard
                key={g.group_id}
                group={g}
                onOpen={() => navigate(`/groups/${g.group_id}`)}
                onEdit={() => { setEditing(g); setShowCreate(true); }}
                onActivate={() => activate(g)}
                onDeactivate={() => deactivate(g)}
                onDuplicate={() => duplicate(g)}
                onDelete={() => remove(g)}
              />
            ))}
          </div>
        )}
      </section>

      <BottomNav />

      {showCreate && (
        <CreateOrEditGroupModal
          existing={editing}
          devices={devices}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={onCreateDone}
        />
      )}
    </div>
  );
}

function Stat({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.025]">
      <div className="flex items-center gap-2">
        <div className="size-8 rounded-lg grid place-items-center" style={{ background: `${color}22` }}>
          <Icon size={14} style={{ color }}/>
        </div>
        <div className="label-eyebrow">{label}</div>
      </div>
      <div className="font-display font-black text-3xl tracking-tighter mt-2">{value}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{sub}</div>
    </div>
  );
}

function GroupCard({ group, onOpen, onEdit, onActivate, onDeactivate, onDuplicate, onDelete }) {
  const [menu, setMenu] = useState(false);
  const isLive = group.is_active;
  return (
    <div
      data-testid={`group-card-${group.group_id}`}
      className={`p-4 rounded-2xl border ${isLive ? "border-[#34C759]/30 bg-[#34C759]/5" : "border-white/10 bg-white/[0.025]"}`}
    >
      <div className="flex items-center gap-3">
        <button onClick={onOpen} className="flex-1 flex items-center gap-3 min-w-0 text-left" data-testid={`open-group-${group.group_id}`}>
          <div className={`size-11 rounded-full grid place-items-center ${isLive ? "bg-[#34C759]/15" : "bg-zinc-800"}`}>
            <Users size={18} className={isLive ? "text-[#34C759]" : "text-zinc-400"}/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="font-display font-bold truncate">{group.name}</div>
              {isLive ? (
                <span className="text-[10px] font-black uppercase tracking-wider text-[#34C759] inline-flex items-center gap-1">
                  <span className="size-1.5 rounded-full bg-[#34C759] animate-pulse"/> Live
                </span>
              ) : group.status === "scheduled" ? (
                <span className="text-[10px] font-black uppercase tracking-wider text-[#FFCC00] inline-flex items-center gap-1">
                  <Calendar size={9}/> Scheduled
                </span>
              ) : (
                <span className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Off</span>
              )}
            </div>
            <div className="text-xs text-zinc-500 truncate">
              {group.member_count} {group.member_count === 1 ? "device" : "devices"}
              {group.status === "scheduled" && group.start_at && (
                <> · {new Date(group.start_at).toLocaleString()} → {new Date(group.end_at).toLocaleString()}</>
              )}
            </div>
          </div>
        </button>
        <div className="relative">
          <button
            data-testid={`group-menu-${group.group_id}`}
            onClick={() => setMenu(!menu)}
            className="size-9 rounded-full border border-white/10 grid place-items-center hover:bg-white/5"
          >
            <MoreHorizontal size={14}/>
          </button>
          {menu && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setMenu(false)}/>
              <div className="absolute right-0 top-11 z-40 w-52 p-1.5 rounded-xl border border-white/10 bg-zinc-950 shadow-2xl">
                <MenuItem icon={Edit3} label="Rename & members" onClick={() => { setMenu(false); onEdit(); }} testid={`menu-edit-${group.group_id}`}/>
                {isLive
                  ? <MenuItem icon={Pause} label="Deactivate" onClick={() => { setMenu(false); onDeactivate(); }} testid={`menu-deactivate-${group.group_id}`}/>
                  : <MenuItem icon={Play} label="Activate now" onClick={() => { setMenu(false); onActivate(); }} testid={`menu-activate-${group.group_id}`}/>}
                <MenuItem icon={Copy} label="Duplicate" onClick={() => { setMenu(false); onDuplicate(); }} testid={`menu-duplicate-${group.group_id}`}/>
                <div className="my-1 h-px bg-white/5"/>
                <MenuItem icon={Trash2} label="Delete" onClick={() => { setMenu(false); onDelete(); }} testid={`menu-delete-${group.group_id}`} danger/>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left hover:bg-white/5 ${danger ? "text-[#FF3B30]" : ""}`}
    >
      <Icon size={14}/> {label}
    </button>
  );
}

function CreateOrEditGroupModal({ existing, devices, onClose, onSaved }) {
  const [name, setName] = useState(existing?.name || "");
  const [status, setStatus] = useState(existing?.status === "scheduled" ? "scheduled" : "live");
  const [startAt, setStartAt] = useState(existing?.start_at ? toLocalInput(existing.start_at) : "");
  const [endAt, setEndAt] = useState(existing?.end_at ? toLocalInput(existing.end_at) : "");
  const [tz, setTz] = useState(existing?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [selected, setSelected] = useState(new Set(existing?.members || []));
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Group needs a name"); return; }
    if (status === "scheduled" && (!startAt || !endAt)) { toast.error("Scheduled groups need start and end times"); return; }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        status,
        member_ids: Array.from(selected),
        ...(status === "scheduled" ? {
          start_at: new Date(startAt).toISOString(),
          end_at: new Date(endAt).toISOString(),
          timezone: tz,
        } : { start_at: null, end_at: null }),
      };
      if (existing) {
        await api.patch(`/groups/${existing.group_id}`, body);
        toast.success("Group updated");
      } else {
        await api.post("/groups", body);
        toast.success("Group created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        data-testid="group-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg my-8 rounded-3xl border border-white/10 bg-zinc-950 p-6"
      >
        <div className="flex items-center justify-between mb-1">
          <div className="label-eyebrow">{existing ? "Edit group" : "New group"}</div>
          <button onClick={onClose} className="size-8 rounded-full grid place-items-center text-zinc-500 hover:text-zinc-100"><X size={16}/></button>
        </div>
        <h2 className="font-display font-black text-2xl tracking-tighter">{existing ? "Edit Group" : "Create Group"}</h2>

        <div className="mt-5 space-y-4">
          <div>
            <div className="label-eyebrow mb-2">Group name</div>
            <input
              data-testid="group-name-input"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 50))}
              placeholder="e.g. Auburn Knights"
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#007AFF]"
            />
          </div>

          <div>
            <div className="label-eyebrow mb-2">Status</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="status-live"
                onClick={() => setStatus("live")}
                className={`p-3 rounded-xl border text-left ${status === "live" ? "border-[#34C759]/40 bg-[#34C759]/8" : "border-white/10 bg-white/[0.02]"}`}
              >
                <div className="flex items-center gap-2"><Radio size={14} className="text-[#34C759]"/><span className="font-bold text-sm">Live Now</span></div>
                <div className="text-xs text-zinc-500 mt-0.5">Active immediately</div>
              </button>
              <button
                data-testid="status-scheduled"
                onClick={() => setStatus("scheduled")}
                className={`p-3 rounded-xl border text-left ${status === "scheduled" ? "border-[#FFCC00]/40 bg-[#FFCC00]/8" : "border-white/10 bg-white/[0.02]"}`}
              >
                <div className="flex items-center gap-2"><Calendar size={14} className="text-[#FFCC00]"/><span className="font-bold text-sm">Scheduled</span></div>
                <div className="text-xs text-zinc-500 mt-0.5">Activates automatically</div>
              </button>
            </div>
          </div>

          {status === "scheduled" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="label-eyebrow mb-2">Start</div>
                <input
                  data-testid="group-start"
                  type="datetime-local"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#FFCC00]"
                />
              </div>
              <div>
                <div className="label-eyebrow mb-2">End</div>
                <input
                  data-testid="group-end"
                  type="datetime-local"
                  value={endAt}
                  onChange={(e) => setEndAt(e.target.value)}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#FFCC00]"
                />
              </div>
              <div className="sm:col-span-2">
                <div className="label-eyebrow mb-2">Timezone</div>
                <input
                  data-testid="group-tz"
                  value={tz}
                  onChange={(e) => setTz(e.target.value)}
                  className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#FFCC00]"
                />
              </div>
            </div>
          )}

          <div>
            <div className="label-eyebrow mb-2 flex items-center justify-between">
              <span>Assign devices · {selected.size}</span>
              {devices.length > 0 && (
                <button
                  onClick={() => setSelected(selected.size === devices.length ? new Set() : new Set(devices.map((d) => d.user_id)))}
                  className="text-[11px] font-bold text-[#007AFF] normal-case tracking-normal"
                >
                  {selected.size === devices.length ? "Clear all" : "Select all"}
                </button>
              )}
            </div>
            {devices.length === 0 ? (
              <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02] text-xs text-zinc-500 text-center">
                No devices yet. Invite some from <Link to="/devices" className="text-[#007AFF] font-bold">Manage My Devices</Link>.
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto rounded-xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
                {devices.map((d, idx) => {
                  const checked = selected.has(d.user_id);
                  return (
                    <button
                      key={d.user_id}
                      data-testid={`device-toggle-${d.user_id}`}
                      onClick={() => toggle(d.user_id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-white/[0.03] ${checked ? "bg-[#007AFF]/10" : ""}`}
                    >
                      <div className={`size-5 rounded grid place-items-center border ${checked ? "bg-[#007AFF] border-[#007AFF]" : "border-white/20"}`}>
                        {checked && <Check size={12} className="text-white"/>}
                      </div>
                      <span className="font-mono text-[10px] text-zinc-500">#{idx + 2}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{d.device_name}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-full border border-white/10 font-bold">Cancel</button>
          <button
            data-testid="save-group-btn"
            onClick={save}
            disabled={saving}
            className="flex-1 py-3 rounded-full bg-[#007AFF] text-white font-bold disabled:opacity-50"
          >
            {saving ? "Saving…" : (existing ? "Save changes" : "Create group")}
          </button>
        </div>
      </div>
    </div>
  );
}

function toLocalInput(iso) {
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}
